import type { ReactNode } from 'react';
import { useBusiness } from '@/features/business/BusinessProvider';

// Mirrors backend/src/common/permissions.ts. The UI only hides what a role cannot do; the API is
// the enforcement point and rejects anything else.
export const P = {
  BusinessUpdate: 'business.update',
  BusinessDelete: 'business.delete',
  ReviewsReply: 'reviews.reply',
  ReviewsTag: 'reviews.tag',
  ReviewsReport: 'reviews.report',
  InvitationsRead: 'invitations.read',
  InvitationsCreate: 'invitations.create',
  TemplatesManage: 'templates.manage',
  CustomersRead: 'customers.read',
  CustomersWrite: 'customers.write',
  ProductsWrite: 'products.write',
  LocationsWrite: 'locations.write',
  AnalyticsRead: 'analytics.read',
  WebhooksManage: 'webhooks.manage',
  ApiKeysManage: 'apiKeys.manage',
  TeamManage: 'team.manage',
  BillingRead: 'billing.read',
  BillingManage: 'billing.manage',
  WidgetsManage: 'widgets.manage',
} as const;
export type Permission = (typeof P)[keyof typeof P];

export function useCan(): (p: Permission) => boolean {
  const { context } = useBusiness();
  const set = new Set(context?.permissions ?? []);
  return (p) => set.has(p);
}

export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return useCan()(permission) ? children : fallback;
}
